import { useEffect, useRef, useState } from "react";
import { execute } from "@/components/terminal/commands";
import { useVisitor } from "@/context/VisitorContext";
import { useWindows } from "@/context/WindowContext";
import { downloadResume } from "@/utils/download";

function motd() {
  return [
    {
      cls: "text-slate-500",
      text: `Last login: ${new Date().toLocaleString("en-GB", { hour12: false })} on ttys000`,
    },
    { cls: "text-sky-300", text: "anish@portfolio — interactive zsh session" },
    { cls: "text-slate-300", text: "Type `help` to list commands. Try `who is he`, `about`, `skills`, `contact`." },
  ];
}

export default function Terminal() {
  const { open, close } = useWindows();
  const { name, setPromptOpen } = useVisitor();
  const [lines, setLines] = useState(motd);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const history = useRef([]);
  const hIdx = useRef(-1);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const ctx = { name, open, close, download: downloadResume, askName: () => setPromptOpen(true) };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, busy]);

  const run = (raw) => {
    const res = execute(raw, ctx);
    if (res.clear) {
      setLines([]);
      setInput("");
      return;
    }
    setLines((l) => [...l, { kind: "cmd", text: raw.trim() }]);
    setBusy(true);
    res.lines.forEach((o, i) => setTimeout(() => setLines((l) => [...l, o]), 120 + i * 65));
    setTimeout(() => {
      if (res.action) res.action();
      setBusy(false);
    }, 150 + res.lines.length * 65);
  };

  const onKeyDown = (e) => {
    if (e.key === "l" && e.ctrlKey) {
      e.preventDefault();
      setLines([]);
      return;
    }
    if (e.key === "Enter" && !busy) {
      const raw = input;
      if (raw.trim()) {
        history.current.push(raw.trim());
        hIdx.current = history.current.length;
      }
      run(raw);
      setInput("");
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (hIdx.current > 0) {
        hIdx.current -= 1;
        setInput(history.current[hIdx.current] || "");
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (hIdx.current < history.current.length - 1) {
        hIdx.current += 1;
        setInput(history.current[hIdx.current]);
      } else {
        hIdx.current = history.current.length;
        setInput("");
      }
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      const pool = ["help", "whoami", "about", "skills", "experience", "education", "neofetch", "contact", "resume", "clock", "ls", "cat ", "open ", "clear", "echo ", "date", "exit"];
      const match = pool.find((c) => input && c.startsWith(input));
      if (match) setInput(match);
    }
  };

  return (
    <div
      className="flex h-full flex-col bg-[#0d1117]/95 font-mono text-[12.5px] leading-6 select-text"
      data-testid="terminal"
      onClick={() => inputRef.current && inputRef.current.focus()}
    >
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {lines.map((l, i) =>
          l.kind === "cmd" ? (
            <p key={i} className="whitespace-pre-wrap break-words">
              <span className="text-green-400">➜</span> <span className="text-sky-300">~</span>{" "}
              <span className="text-slate-100">{l.text}</span>
            </p>
          ) : (
            <p key={i} className={`whitespace-pre-wrap break-words ${l.cls || "text-slate-300"}`}>
              {l.text}
            </p>
          )
        )}
        {busy && (
          <p className="text-slate-600" data-testid="terminal-busy">
            <span className="text-sonoma">▮</span> working…
          </p>
        )}
        <div className="flex items-center gap-2 py-0.5">
          <span className="shrink-0">
            <span className="text-green-400">➜</span> <span className="text-sky-300">~</span>
          </span>
          <input
            ref={inputRef}
            data-testid="terminal-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={busy}
            autoFocus
            spellCheck={false}
            autoComplete="off"
            aria-label="Terminal input"
            className="w-full bg-transparent text-slate-100 caret-blue-400 outline-none placeholder:text-slate-600"
          />
        </div>
      </div>
    </div>
  );
}
